import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";

// Modal que pergunta quais seções incluir no PDF do roteiro antes de gerar.
// Defaults por status: Rascunho/EmMontagem = tudo false (PDF só com a descrição
// narrativa, formato de orçamento); Confirmado/Concluído = tudo true (roteiro
// fechado, mostra passageiros + dias + valores). Luciana pode override manual.
//
// StatusRoteiro: 0 Rascunho · 1 EmMontagem · 2 Confirmado · 3 Cancelado · 4 Concluído

function defaultsPorStatus(status) {
  const orcamento = status === 0 || status === 1; // Rascunho ou EmMontagem
  return {
    passageiros: !orcamento,
    dias: !orcamento,
    valores: !orcamento,
  };
}

export default function PdfOptionsModal({ open, roteiro, actionLabel, onClose, onConfirm }) {
  const { C } = useTheme();
  const [options, setOptions] = useState(defaultsPorStatus(roteiro?.status ?? 0));

  // Reset defaults sempre que abrir (roteiro pode ter status diferente entre aberturas)
  useEffect(() => {
    if (open) setOptions(defaultsPorStatus(roteiro?.status ?? 0));
  }, [open, roteiro?.status]);

  function toggle(key) {
    setOptions((o) => ({ ...o, [key]: !o[key] }));
  }

  function confirm() {
    onConfirm?.(options);
  }

  const rows = [
    { key: "passageiros", label: "Passageiros", hint: "Nome, passaporte, telefone do grupo" },
    { key: "dias", label: "Dia a dia", hint: "Itinerário formal (Dia 1, Dia 2...) — cada dia numa página" },
    { key: "valores", label: "Valores e forma de pagamento", hint: "Valor total, PIX, dados bancários" },
  ];

  return (
    <Modal
      open={open}
      title="Como imprimir o PDF?"
      subtitle={roteiro?.titulo}
      onClose={onClose}
      width={480}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {rows.map((r) => (
          <label
            key={r.key}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "12px 14px",
              background: C.bg,
              borderRadius: 10,
              border: `1px solid ${options[r.key] ? C.accent : C.border}`,
              cursor: "pointer",
              transition: "all 0.12s",
            }}
          >
            <input
              type="checkbox"
              checked={options[r.key]}
              onChange={() => toggle(r.key)}
              style={{ marginTop: 3, cursor: "pointer" }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.label}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{r.hint}</div>
            </div>
          </label>
        ))}
      </div>

      <div
        style={{
          fontSize: 11,
          color: C.textMuted,
          background: C.bg,
          padding: "10px 14px",
          borderRadius: 8,
          marginBottom: 18,
          lineHeight: 1.5,
        }}
      >
        💡 A descrição do roteiro (o texto principal) sempre aparece — as opções
        acima controlam só as seções extras.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: "transparent",
            color: C.textMuted,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirm}
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            border: "none",
            background: C.accent,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {actionLabel ?? "Gerar PDF"}
        </button>
      </div>
    </Modal>
  );
}
